import {
  Component,
  effect,
  inject,
  input,
  output,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { MarketingPricingService, PricingCalendarDay } from 'src/shared/services/marketing-pricing.service';

interface CalendarCell {
  day: number | null;
  data?: PricingCalendarDay;
}

@Component({
  selector: 'pricing-calendar',
  standalone: true,
  imports: [CommonModule, TidyUiComponentsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <tidy-card>
      <div class="pricing-calendar-nav">
        <button
          type="button"
          class="pricing-calendar-nav-arrow"
          aria-label="Previous month"
          (click)="prevMonth()">
          <span
            class="pricing-calendar-nav-icon"
            [style.-webkit-mask-image]="'url(assets/svg/chevron-back-outline.svg)'"
            [style.mask-image]="'url(assets/svg/chevron-back-outline.svg)'"></span>
        </button>
        <tidy-text [header]="monthLabel" />
        <button
          type="button"
          class="pricing-calendar-nav-arrow"
          aria-label="Next month"
          (click)="nextMonth()">
          <span
            class="pricing-calendar-nav-icon"
            [style.-webkit-mask-image]="'url(assets/svg/chevron-forward-outline.svg)'"
            [style.mask-image]="'url(assets/svg/chevron-forward-outline.svg)'"></span>
        </button>
      </div>

      <div class="pricing-calendar-grid">
        <div class="pricing-calendar-header">
          @for (day of weekDays; track day) {
            <div class="pricing-calendar-header-cell">{{ day }}</div>
          }
        </div>
        @for (week of calendarWeeks; track $index) {
          <div class="pricing-calendar-row">
            @for (cell of week; track $index) {
              <div
                class="pricing-calendar-cell"
                [class.empty]="!cell.day"
                [class.clickable]="cell.day && cell.data"
                [class.unavailable]="cell.data && !cell.data.available"
                [class.weekend]="cell.data?.minStay === 2"
                (click)="onCellClick(cell)">
                @if (cell.day) {
                  <span class="pricing-calendar-day">{{ cell.day }}</span>
                  @if (cell.data) {
                    <span class="pricing-calendar-price">
                      {{ cell.data.available ? '$' + (cell.data.price / 100) : '-' }}
                    </span>
                  }
                }
              </div>
            }
          </div>
        }
      </div>
    </tidy-card>
  `,
  styles: [`
    .pricing-calendar-nav {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 16px;
    }
    .pricing-calendar-nav-arrow {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 8px 14px;
      background: transparent;
      border: none;
      cursor: pointer;
    }
    .pricing-calendar-nav-icon {
      display: block;
      width: 16px;
      height: 16px;
      background-color: var(--secondary-text);
      -webkit-mask-size: contain;
      mask-size: contain;
      -webkit-mask-repeat: no-repeat;
      mask-repeat: no-repeat;
      -webkit-mask-position: center;
      mask-position: center;
    }
    .pricing-calendar-nav-arrow:hover .pricing-calendar-nav-icon {
      background-color: var(--tidy-text-primary);
    }
    .pricing-calendar-grid {
      margin-top: 10px;
    }
    .pricing-calendar-header {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      text-align: center;
      font-weight: 600;
      font-size: 12px;
      color: #6b7280;
      padding-bottom: 8px;
    }
    .pricing-calendar-header-cell {
      padding: 4px;
    }
    .pricing-calendar-row {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      border-top: 1px solid var(--tidy-border-color);
    }
    .pricing-calendar-cell {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 10px 2px;
      min-height: 72px;
    }
    .pricing-calendar-cell.empty {
      background: transparent;
    }
    .pricing-calendar-cell.unavailable {
      opacity: 0.4;
    }
    .pricing-calendar-cell.weekend {
      background: rgba(66, 149, 163, 0.15);
    }
    .pricing-calendar-cell.clickable {
      cursor: pointer;
      transition: background 0.12s;
    }
    .pricing-calendar-cell.clickable:hover {
      background: rgba(66, 149, 163, 0.22);
    }
    .pricing-calendar-day {
      font-size: 10px;
      font-weight: 500;
    }
    .pricing-calendar-price {
      font-size: 11px;
      font-weight: 600;
      margin-top: 2px;
    }
  `]
})
export class PricingCalendarComponent {
  address = input.required<any>();
  mock = input<boolean>(false);
  loaded = output<void>();
  cellClick = output<PricingCalendarDay>();

  onCellClick(cell: CalendarCell): void {
    if (cell.day && cell.data) {this.cellClick.emit(cell.data);}
  }

  private readonly pricingService = inject(MarketingPricingService);
  private readonly cdr = inject(ChangeDetectorRef);

  currentYear: number;
  currentMonth: number;
  monthLabel = '';
  weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  calendarWeeks: CalendarCell[][] = [];

  constructor() {
    effect(() => {
      // Re-read the signals so the effect re-runs when address or mock changes,
      // then reset to the current month and rebuild for the new context.
      this.address();
      this.mock();
      const now = new Date();
      this.currentYear = now.getFullYear();
      this.currentMonth = now.getMonth() + 1;
      this.loadData();
    });
  }

  prevMonth() {
    this.currentMonth--;
    if (this.currentMonth < 1) {
      this.currentMonth = 12;
      this.currentYear--;
    }
    this.loadData();
  }

  nextMonth() {
    this.currentMonth++;
    if (this.currentMonth > 12) {
      this.currentMonth = 1;
      this.currentYear++;
    }
    this.loadData();
  }

  async loadData() {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    this.monthLabel = `${monthNames[this.currentMonth - 1]} ${this.currentYear}`;

    const daysInMonth = new Date(this.currentYear, this.currentMonth, 0).getDate();
    const dateFrom = `${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}-01`;
    const dateTo = `${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;

    try {
      const days = this.mock()
        ? this.buildMockDays(this.currentYear, this.currentMonth, daysInMonth)
        : await this.pricingService.getCalendarData(this.address().id, dateFrom, dateTo);
      // Defensive: only render days that fall within the current month, regardless
      // of what the backend returns — otherwise extra days leak into the grid and
      // the calendar visually overflows into adjacent months.
      const monthPrefix = `${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}-`;
      const monthDays = days.filter(d => d.date.startsWith(monthPrefix));
      const firstDayOfWeek = new Date(this.currentYear, this.currentMonth - 1, 1).getDay();

      const cells: CalendarCell[] = [];
      for (let i = 0; i < firstDayOfWeek; i++) {
        cells.push({ day: null });
      }
      for (const dayData of monthDays) {
        const dayNum = parseInt(dayData.date.split('-')[2], 10);
        cells.push({ day: dayNum, data: dayData });
      }
      while (cells.length % 7 !== 0) {
        cells.push({ day: null });
      }

      this.calendarWeeks = [];
      for (let i = 0; i < cells.length; i += 7) {
        this.calendarWeeks.push(cells.slice(i, i + 7));
      }
      this.cdr.markForCheck();
    } catch (err) {
      console.error('Failed to load calendar data', err);
    } finally {
      this.loaded.emit();
    }
  }

  private buildMockDays(year: number, month: number, daysInMonth: number): PricingCalendarDay[] {
    // Deterministic mock pricing for the about-this-page preview.
    // Base $250/night, +$75 on Fri/Sat, plus a small day-of-month variance for visual interest.
    // A handful of days are marked unavailable to mimic bookings.
    const unavailableDays = new Set([8, 9, 17, 23]);
    const days: PricingCalendarDay[] = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dayOfWeek = new Date(year, month - 1, d).getDay();
      const isFriOrSat = dayOfWeek === 5 || dayOfWeek === 6;
      const basePrice = 25000;
      const weekendBoost = isFriOrSat ? 7500 : 0;
      const variance = ((d * 7) % 6) * 1000;
      days.push({
        date: dateStr,
        price: basePrice + weekendBoost + variance,
        minStay: isFriOrSat ? 2 : 1,
        available: !unavailableDays.has(d),
        source: 'base'
      });
    }
    return days;
  }
}
